(() => {
  if (window.hobbytronNativeSearchReady) return;
  window.hobbytronNativeSearchReady = true;
  const config = () => JSON.parse(document.querySelector('[data-ht-search-config]')?.textContent || '{}');
  const suffix = () => config().exclusions || '';
  const legacySuffix = ' -title:Mojis -title:"Mrs Bench"';
  const scoped = value => `${value}${suffix() ? ` ${suffix()}` : ''}`;
  const quote = term => JSON.stringify(term.trim());
  const normalQuery = term => term.trim().replace(/[():"\\*]/g, ' ').trim() || '*';
  const canonicalSku = term => {
    const match = term.trim().match(/^(?:([a-z]{1,8})[- ]?)?(\d{3,}[a-z]?(?:-[a-z0-9]+)*)$/i);
    return match ? `${(match[1] || 'ZX').toUpperCase()}-${match[2].toUpperCase()}` : null;
  };
  const partialSkuQuery = term => scoped(`variants.sku:${term}*`);
  const skuQuery = term => scoped(`variants.sku:${quote(term)}`);
  const fallbackQuery = term => scoped(`(variants.sku:${quote(term)} OR ${quote(term)})`);
  const termFrom = query => {
    let value = query.trim();
    if (suffix() && value.endsWith(suffix())) value = value.slice(0, -suffix().length).trim();
    if (value.endsWith(legacySuffix)) value = value.slice(0, -legacySuffix.length).trim();
    if (value.startsWith('(variants.sku:')) value = value.slice(1).split(' OR ')[0];
    if (value.startsWith('variants.sku:')) value = value.slice('variants.sku:'.length).replace(/\*$/, '');
    if (value === '*') return '';
    try {
      const term = JSON.parse(value);
      return typeof term === 'string' ? term : value;
    } catch { return value; }
  };
  const isResolved = query => {
    const term = termFrom(query);
    const code = canonicalSku(term);
    if (code && code !== term.toUpperCase()) return false;
    return [normalQuery, skuQuery, partialSkuQuery].some(build => build(term) === query);
  };
  const skuCache = new Map();
  const lookup = async (term, context, signal) => {
    if (!term.trim()) return false;
    if (skuCache.has(term)) return skuCache.get(term);
    const url = new URL(config().url || '/search', location.origin);
    // Only query-specific Shopify results are inspected; no whole-catalog download.
    url.searchParams.set('view', 'hobbytron-sku-probe');
    url.searchParams.set('q', skuQuery(term));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    url.searchParams.set('options[unavailable_products]', 'show');
    const preview = context.searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    for (const [key, value] of context.searchParams) if (key.startsWith('filter.')) url.searchParams.append(key, value);
    const timeout = new AbortController();
    const cancel = () => timeout.abort();
    signal.addEventListener('abort', cancel, { once: true });
    const timer = setTimeout(cancel, 3500);
    try {
      const response = await fetch(url.href, { credentials: 'same-origin', signal: timeout.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Native SKU lookup unavailable');
      const result = await response.json();
      if (!result.complete || !Array.isArray(result.matches)) throw new Error('Incomplete native SKU lookup');
      const found = result.matches.some(item => String(item.sku).trim().toLocaleLowerCase() === term.trim().toLocaleLowerCase());
      if (skuCache.size > 30) skuCache.clear();
      skuCache.set(term, found);
      return found;
    } finally { clearTimeout(timer); signal.removeEventListener('abort', cancel); }
  };
  const resolveQuery = async (term, context, signal) => {
    const code = canonicalSku(term);
    try {
      if (code) return await lookup(code, context, signal) ? skuQuery(code) : partialSkuQuery(code);
      const candidates = [...new Set(term.trim().split(/\s+/).map(canonicalSku).filter(Boolean))].slice(0, 2);
      for (const candidate of candidates) if (await lookup(candidate, context, signal)) return skuQuery(candidate);
      return normalQuery(term);
    } catch (error) {
      if (signal.aborted) throw error;
      return code ? partialSkuQuery(code) : normalQuery(term);
    }
  };
  const parameters = url => {
    if (config().brand === 'mojis') url.searchParams.delete('view');
    else url.searchParams.set('view', 'hobbytron-shell');
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[unavailable_products]', 'show');
    return url;
  };
  let predictiveId = 0;
  const suggestionCache = new Map();
  const predictive = (form, input) => {
    const id = `NativeSearch-${++predictiveId}`;
    const panel = document.createElement('div');
    panel.className = 'native-search-panel';
    panel.dataset.brand = config().brand || 'hobbytron';
    panel.hidden = true;
    const status = document.createElement('p');
    status.className = 'native-search-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    const list = document.createElement('ul');
    list.id = id;
    list.className = 'native-search-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Product suggestions');
    const all = document.createElement('a');
    all.className = 'native-search-all';
    all.textContent = 'View all results';
    panel.append(status, list, all);
    (form.closest('.ht-shell-search-dialog__inner') || form).after(panel);
    input.removeAttribute('aria-owns');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('autocomplete', 'off');
    input.maxLength = 200;
    let controller, timer, active = -1, resolvedQuery = '';
    const close = () => {
      panel.hidden = true;
      active = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    };
    const safeUrl = value => {
      const url = new URL(value, location.origin);
      if (!['http:', 'https:'].includes(url.protocol) || ![location.hostname, 'hobbytron.com', 'mojistoys.com', 'www.mojistoys.com'].includes(url.hostname)) return null;
      return url.href;
    };
    const render = (products, term) => {
      list.replaceChildren();
      active = -1;
      input.removeAttribute('aria-activedescendant');
      const seen = new Set();
      products.sort((a, b) => Number(b.exactSku) - Number(a.exactSku));
      for (const product of products) {
        const href = safeUrl(product.url);
        if (!href || seen.has(product.productId)) continue;
        seen.add(product.productId);
        const item = document.createElement('li');
        item.setAttribute('role', 'presentation');
        const link = document.createElement('a');
        link.id = `${id}-${seen.size}`;
        link.className = 'native-search-option';
        link.href = href;
        link.tabIndex = -1;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
        if (product.image) {
          const source = new URL(product.image, location.origin);
          if (['http:', 'https:'].includes(source.protocol)) {
            const img = document.createElement('img');
            img.src = source.href;
            img.alt = '';
            img.width = img.height = 64;
            link.append(img);
          }
        }
        const text = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = product.title;
        const price = document.createElement('small');
        price.textContent = `${product.price}${product.available ? '' : ' · Sold out'}`;
        text.append(title, price);
        if (product.exactSku) {
          const code = document.createElement('small');
          code.textContent = `Exact SKU: ${product.sku}`;
          text.append(code);
        }
        link.append(text);
        item.append(link);
        list.append(item);
      }
      status.textContent = seen.size ? `${seen.size} product suggestions` : 'No matching products. Try a shorter name, another spelling or the full SKU.';
      const target = parameters(new URL(config().url || '/search', location.origin));
      target.searchParams.set('q', resolvedQuery || normalQuery(term));
      all.href = target.href;
      all.textContent = seen.size ? 'View all results' : config().brand === 'mojis' ? 'Browse all Mojis' : 'Browse all toys';
      if (!seen.size) all.href = config().brand === 'mojis' ? '/collections/mojis-squish-lab' : '/collections/hobbytron-hotfix-shop-all-2026-10-07';
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    const run = async () => {
      const term = input.value.trim();
      if (!term) { close(); return; }
      controller?.abort();
      controller = new AbortController();
      const current = controller;
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      list.replaceChildren();
      all.hidden = true;
      active = -1;
      input.removeAttribute('aria-activedescendant');
      status.textContent = 'Searching…';
      try {
        resolvedQuery = await resolveQuery(term, new URL(location.href), current.signal);
        const key = `${config().brand}|${resolvedQuery}`;
        let products = suggestionCache.get(key);
        if (!products) {
          const url = new URL(config().suggestUrl || '/search/suggest', location.origin);
          const predictiveQuery = suffix() && resolvedQuery.endsWith(suffix()) ? resolvedQuery.slice(0, -suffix().length).trim() : resolvedQuery;
          url.searchParams.set('q', predictiveQuery);
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '10');
          url.searchParams.set('resources[options][unavailable_products]', 'show');
          url.searchParams.set('section_id', 'hobbytron-native-predictive');
          const response = await fetch(url.href, {credentials:'same-origin', signal:current.signal});
          if (!response.ok) throw new Error('Predictive search unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = doc.querySelector('[data-native-search-products]');
          if (!payload) throw new Error('Predictive search response missing');
          products = JSON.parse(payload.textContent).products;
          if (!Array.isArray(products)) throw new Error('Invalid search results');
          if (suggestionCache.size > 20) suggestionCache.clear();
          suggestionCache.set(key, products);
        }
        if (current.signal.aborted || input.value.trim() !== term) return;
        all.hidden = false;
        render([...products], term);
      } catch (error) {
        if (current.signal.aborted) return;
        status.textContent = 'Suggestions are unavailable. Press Search to see results.';
        all.hidden = true;
      }
    };
    input.addEventListener('input', event => {
      clearTimeout(timer);
      controller?.abort();
      close();
      if (event.isComposing || !input.value.trim()) return;
      timer = setTimeout(run, 220);
    });
    input.addEventListener('compositionend', () => { clearTimeout(timer); timer = setTimeout(run, 220); });
    input.addEventListener('focus', () => { if (input.value.trim() && list.children.length) { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); } });
    input.addEventListener('keydown', event => {
      const options = [...list.querySelectorAll('[role=option]')];
      if (event.key === 'Escape' && !panel.hidden) { event.preventDefault(); event.stopPropagation(); controller?.abort(); clearTimeout(timer); close(); return; }
      if (panel.hidden) return;
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && options.length) {
        event.preventDefault();
        active = (active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((node, index) => node.setAttribute('aria-selected', String(index === active)));
        input.setAttribute('aria-activedescendant', options[active].id);
        options[active].scrollIntoView({block:'nearest'});
      } else if (event.key === 'Enter' && active >= 0 && options[active]) {
        event.preventDefault();
        location.assign(options[active].href);
      } else if (event.key === 'Tab') close();
    });
    form.addEventListener('reset', () => { clearTimeout(timer); controller?.abort(); close(); });
    document.addEventListener('pointerdown', event => { if (!form.contains(event.target) && !panel.contains(event.target)) close(); });
    form.closest('dialog')?.addEventListener('close', () => { controller?.abort(); clearTimeout(timer); close(); });
  };

  const init = root => {
    root.querySelectorAll('[data-ht-shell-search]:not([data-ht-search-ready])').forEach(form => {
      const input = form.querySelector('[data-ht-search-input]');
      const query = form.querySelector('[name="q"]');
      if (!input || !query) return;
      form.dataset.htSearchReady = 'true';
      input.value = termFrom(input.value);
      predictive(form, input);
      let controller;
      form.addEventListener('reset', () => { controller?.abort(); form.removeAttribute('aria-busy'); });
      form.addEventListener('submit', async event => {
        event.preventDefault();
        controller?.abort();
        controller = new AbortController();
        const active = controller;
        const term = input.value.trim();
        input.value = term;
        form.setAttribute('aria-busy', 'true');
        // A new query resets filters/pages as the existing GET form did.
        const target = parameters(new URL(form.action, location.href));
        const current = new URL(location.href);
        const preview = new FormData(form).get('preview_theme_id') || current.searchParams.get('preview_theme_id');
        if (preview) target.searchParams.set('preview_theme_id', preview);
        try {
          const resolved = await resolveQuery(term, target, active.signal);
          if (active.signal.aborted) return;
          query.value = resolved;
          target.searchParams.set('q', resolved);
          location.assign(target.href);
        } catch (error) { if (!active.signal.aborted) throw error; }
        finally { if (controller === active) form.removeAttribute('aria-busy'); }
      });
    });
    const guard = root.querySelector('[data-ht-search-scope-guard]');
    const results = root.querySelector('[data-ht-search-query]');
    const currentQuery = guard ? guard.dataset.query : results?.dataset.htSearchQuery;
    if (currentQuery === undefined || !termFrom(currentQuery).trim() || isResolved(currentQuery)) return;
    const current = new URL(location.href);
    const isShell = !!guard || results?.dataset.htSearchScoped === 'true';
    const controller = new AbortController();
    resolveQuery(termFrom(currentQuery), current, controller.signal).then(resolved => {
      // Legacy title search retains its native presentation; exact SKU bookmarks
      // enter the same product-search experience as the shared header.
      if (!isShell && !canonicalSku(termFrom(currentQuery)) && !resolved.startsWith('variants.sku:')) return;
      const target = parameters(current);
      target.searchParams.set('q', resolved);
      target.searchParams.delete('page');
      location.replace(target.href);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
